import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Chip,
  IconButton,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { createGuidedActivity } from '../../services/templateService';
import GroupedNodeList from './GroupedNodeList';
import PartnerSelector from './PartnerSelector';

const GuidedTemplateWizard = ({ open, onClose, onActivityCreated, flowConfig }) => {
  const { t } = useTranslation('homepage');
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const chatModes = React.useMemo(() => [
    { value: 'sticker', label: t('wizard.chatMode.sticker'), description: t('wizard.chatMode.stickerDesc') },
    { value: 'chatroom', label: t('wizard.chatMode.chatroom'), description: t('wizard.chatMode.chatroomDesc') },
    { value: 'tagged-forum', label: t('wizard.chatMode.taggedForum'), description: t('wizard.chatMode.taggedForumDesc') }
  ], [t]);
  
  // 活動資料
  const [activityData, setActivityData] = useState({
    title: '',
    description: '',
    chatmode: 'sticker',
    duration: 30, // 天數
  });

  // 分組節點資料
  const [nodeGroups, setNodeGroups] = useState([]);

  // AI 夥伴資料
  const [selectedPartners, setSelectedPartners] = useState([]);

  // 動態展開步驟：將 grouped-node-list 展開為多個單獨的群組步驟
  const expandedSteps = React.useMemo(() => {
    if (!flowConfig?.flow?.steps) return [];
    
    const expanded = [];
    flowConfig.flow.steps.forEach(step => {
      if (step.fieldType === 'grouped-node-list' && flowConfig.nodeTemplates?.nodeGroups) {
        // 將每個 nodeGroup 展開為獨立步驟
        flowConfig.nodeTemplates.nodeGroups.forEach((group, index) => {
          expanded.push({
            ...step,
            title: group.groupName,
            description: group.groupDescription || step.description,
            fieldType: 'single-node-group',
            groupIndex: index,
            groupData: group
          });
        });
      } else {
        expanded.push(step);
      }
    });
    return expanded;
  }, [flowConfig]);

  // 根據展開的步驟獲取標題
  const steps = expandedSteps.map(s => s.title);
  const dialogTitle = flowConfig?.name || t('wizard.defaultTitle');

  // 根據流程配置設置預設值
  useEffect(() => {
    if (flowConfig) {
      // 設置活動預設值
      if (flowConfig.activity) {
        setActivityData(prev => ({
          ...prev,
          chatmode: flowConfig.activity.settings?.chatmode || 'sticker',
          duration: parseInt(flowConfig.activity.endDate?.replace('+', '').replace('d', '')) || 30
        }));
      }

      // 初始化分組節點
      if (flowConfig.nodeTemplates?.nodeGroups) {
        const initialGroups = flowConfig.nodeTemplates.nodeGroups.map(group => ({
          groupId: group.groupId,
          groupName: group.groupName,
          groupDescription: group.groupDescription,
          minNodes: group.minNodes,
          maxNodes: group.maxNodes,
          nodes: group.defaultNodes.map(node => ({
            title: node.title || '',
            content: node.content || '',
            tags: node.tags || []
          }))
        }));
        setNodeGroups(initialGroups);
      }
    }
  }, [flowConfig]);

  const handleNext = () => {
    // 獲取當前步驟配置（從展開的步驟中）
    const currentStepConfig = expandedSteps[activeStep];
    
    // 驗證活動基本資訊
    if (currentStepConfig?.fields?.some(f => f.name === 'title')) {
      if (!activityData.title.trim()) {
        setError(t('wizard.titleRequired'));
        return;
      }
    }

    // 驗證單個節點組
    if (currentStepConfig?.fieldType === 'single-node-group') {
      const groupIndex = currentStepConfig.groupIndex;
      const group = nodeGroups[groupIndex];
      if (group) {
        const validNodesInGroup = group.nodes.filter(n => n.title.trim() && n.content.trim());
        if (group.minNodes && validNodesInGroup.length < group.minNodes) {
          setError(t('wizard.minNodesRequired', { count: group.minNodes }));
          return;
        }
      }
    }

    // 驗證分組節點
    if (currentStepConfig?.fieldType === 'grouped-node-list') {
      for (const group of nodeGroups) {
        const validNodes = group.nodes.filter(n => n.title.trim() && n.content.trim());
        if (group.minNodes && validNodes.length < group.minNodes) {
          setError(t('wizard.minNodesForGroup', { group: group.groupName, count: group.minNodes }));
          return;
        }
      }
    }

    setError(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleActivityChange = (field, value) => {
    setActivityData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreate = async () => {
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      setError(t('wizard.loginRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        userId,
        templateId: flowConfig?.id,
        activity: {
          title: activityData.title,
          description: activityData.description,
          chatmode: activityData.chatmode,
          duration: activityData.duration
        }
      };

      // 添加分組節點
      payload.nodeGroups = nodeGroups.map(group => ({
        groupId: group.groupId,
        groupName: group.groupName,
        nodes: group.nodes
          .filter(node => node.title.trim() && node.content.trim())
          .map(node => ({
            title: node.title.trim(),
            content: node.content.trim(),
            tags: node.tags
          }))
      })).filter(group => group.nodes.length > 0);

      // 添加選擇的 AI 夥伴
      if (selectedPartners.length > 0) {
        payload.selectedPartners = selectedPartners;
      }

      const result = await createGuidedActivity(payload);
      
      // 創建成功，設置相關資訊（同時設置 localStorage 和 sessionStorage）
      if (result.activity && result.activity.id) {
        localStorage.setItem('activityId', result.activity.id);
        sessionStorage.setItem('activityId', result.activity.id);
        if (result.group && result.group.id) {
          localStorage.setItem('groupId', result.group.id);
          sessionStorage.setItem('groupId', result.group.id);
        }
        
        // 通知父組件
        if (onActivityCreated) {
          onActivityCreated(result);
        }
        
        // 重置表單並關閉
        handleReset();
        onClose();
      }
    } catch (err) {
      setError(err.message || t('wizard.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setActivityData({
      title: '',
      description: '',
      chatmode: 'sticker',
      duration: 30
    });
    setNodeGroups([]);
    setSelectedPartners([]);
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      handleReset();
      onClose();
    }
  };

  const getStepContent = (stepIndex) => {
    const stepConfig = expandedSteps[stepIndex];
    
    if (!stepConfig) {
      return <Typography color="error">{t('wizard.stepConfigError')}</Typography>;
    }

    // 根據步驟的 fieldType 渲染不同的內容
    switch (stepConfig.fieldType) {
      case 'basic-info':
        // 活動基本資訊
        return (
          <Box>
            {stepConfig.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {stepConfig.description}
              </Typography>
            )}
            {stepConfig.fields.map((field) => {
              if (field.type === 'text') {
                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={field.label}
                    value={activityData[field.name] || ''}
                    onChange={(e) => handleActivityChange(field.name, e.target.value)}
                    margin="normal"
                    required={field.required}
                    helperText={field.placeholder}
                  />
                );
              } else if (field.type === 'textarea') {
                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={field.label}
                    value={activityData[field.name] || ''}
                    onChange={(e) => handleActivityChange(field.name, e.target.value)}
                    margin="normal"
                    multiline
                    rows={3}
                    helperText={field.placeholder}
                  />
                );
              } else if (field.type === 'number') {
                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    type="number"
                    label={field.label}
                    value={activityData[field.name] || ''}
                    onChange={(e) => handleActivityChange(field.name, parseInt(e.target.value))}
                    margin="normal"
                    inputProps={{ min: 1, max: 365 }}
                    helperText={field.placeholder}
                  />
                );
              }
              return null;
            })}
          </Box>
        );

      case 'chatmode-select':
        // 聊天模式選擇
        return (
          <Box>
            {stepConfig.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {stepConfig.description}
              </Typography>
            )}
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 2 }}>
                {t('wizard.selectChatModeLabel')}
              </FormLabel>
              <RadioGroup
                value={activityData.chatmode}
                onChange={(e) => handleActivityChange('chatmode', e.target.value)}
              >
                {chatModes.map((mode) => (
                  <Paper 
                    key={mode.value} 
                    sx={{ 
                      p: 2, 
                      mb: 2,
                      border: activityData.chatmode === mode.value ? 2 : 1,
                      borderColor: activityData.chatmode === mode.value ? 'primary.main' : 'divider'
                    }}
                  >
                    <FormControlLabel
                      value={mode.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {mode.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {mode.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 'partner-selector':
        // AI 夥伴選擇
        return (
          <Box>
            {stepConfig.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {stepConfig.description}
              </Typography>
            )}
            <PartnerSelector
              availablePartners={flowConfig?.flow?.partners?.availablePartners || flowConfig?.partners || []}
              selectedPartners={selectedPartners}
              onChange={setSelectedPartners}
            />
          </Box>
        );

      case 'single-node-group':
        // 單一節點組編輯
        const groupIndex = stepConfig.groupIndex;
        const currentGroup = nodeGroups[groupIndex];
        
        if (!currentGroup) return <Typography color="error">{t('wizard.nodeGroupLoading')}</Typography>;

        const updateGroup = (updater) => {
          setNodeGroups(prev => {
            const newGroups = [...prev];
            updater(newGroups[groupIndex]);
            return newGroups;
          });
        };

        return (
          <Box>
            {stepConfig.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {stepConfig.description}
              </Typography>
            )}
            
            {currentGroup.nodes.map((node, nodeIndex) => (
              <Paper key={nodeIndex} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">{t('wizard.node', { index: nodeIndex + 1 })}</Typography>
                  {(!currentGroup.minNodes || currentGroup.nodes.length > currentGroup.minNodes) && (
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => updateGroup(g => g.nodes.splice(nodeIndex, 1))}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <TextField
                  fullWidth
                  label={t('wizard.nodeTitle')}
                  value={node.title}
                  onChange={(e) => updateGroup(g => g.nodes[nodeIndex].title = e.target.value)}
                  margin="normal"
                  size="small"
                  required
                />

                <TextField
                  fullWidth
                  label={t('wizard.nodeContent')}
                  value={node.content}
                  onChange={(e) => updateGroup(g => g.nodes[nodeIndex].content = e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                  size="small"
                  required
                />
              </Paper>
            ))}

            {(!currentGroup.maxNodes || currentGroup.nodes.length < currentGroup.maxNodes) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => updateGroup(g => g.nodes.push({ title: '', content: '', tags: [] }))}
                  variant="outlined"
                >
                  {t('wizard.addNode')}
                </Button>
              </Box>
            )}
            
            {currentGroup.minNodes && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                {currentGroup.maxNodes
                  ? t('wizard.minMaxNodes', { min: currentGroup.minNodes, max: currentGroup.maxNodes })
                  : t('wizard.minNodes', { count: currentGroup.minNodes })}
              </Typography>
            )}
          </Box>
        );

      case 'grouped-node-list':
        // 分組節點列表（舊格式，保留兼容性）
        return (
          <Box>
            {stepConfig.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {stepConfig.description}
              </Typography>
            )}
            <GroupedNodeList
              nodeGroups={nodeGroups}
              onChange={setNodeGroups}
            />
          </Box>
        );

      case 'confirmation':
        // 確認步驟
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('wizard.confirmInfoTitle')}
            </Typography>
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2">{t('wizard.activityTitleLabel')}</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{activityData.title}</Typography>
              
              {activityData.description && (
                <>
                  <Typography variant="subtitle2">{t('wizard.activityDescriptionLabel')}</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{activityData.description}</Typography>
                </>
              )}
              
              <Typography variant="subtitle2">{t('wizard.chatModeLabel')}</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {chatModes.find(m => m.value === activityData.chatmode)?.label}
              </Typography>
              
              {/* <Typography variant="subtitle2">活動持續時間</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{activityData.duration} 天</Typography> */}

              {selectedPartners.length > 0 && (
                <>
                  <Typography variant="subtitle2">{t('wizard.aiPartnersLabel')}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {selectedPartners.map(ref => {
                      const partner = flowConfig?.flow?.partners?.availablePartners?.find(p => p.reference === ref);
                      return (
                        <Chip key={ref} label={partner?.name || ref} />
                      );
                    })}
                  </Box>
                </>
              )}
              
              <Typography variant="subtitle2">{t('wizard.guidedGroupsLabel')}</Typography>
              {nodeGroups.map(group => (
                <Box key={group.groupId} sx={{ ml: 2, mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">{group.groupName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('wizard.nodeCount', { count: group.nodes.filter(n => n.title.trim() && n.content.trim()).length })}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Box>
        );

      default:
        return <Typography color="error">{t('wizard.unknownStepType', { type: stepConfig?.fieldType })}</Typography>;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', mt: 2 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ mt: 3, mb: 2 }}>
            {getStepContent(activeStep)}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('wizard.cancel')}
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
        >
          {t('wizard.back')}
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? t('wizard.creating') : t('wizard.confirmCreate')}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext}>
            {t('wizard.next')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GuidedTemplateWizard;

